import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useMemo } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import type { UserCostReport, UserCostEntry } from './userCostAnalysisService';

const CATEGORY_COLORS: Record<string, string> = {
  networking: '#f97316',
  compute: '#3b82f6',
  storage: '#22c55e',
};

const PIE_COLORS = ['#3b82f6', '#22c55e', '#a855f7', '#f97316', '#6b7280', '#eab308', '#06b6d4', '#ec4899'];

function formatBRL(value: number) {
  return value.toLocaleString(getInterfaceLanguage(), { style: 'currency', currency: 'BRL' });
}

const RADIAN = Math.PI / 180;

function renderCustomLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }: any) {
  if (percent < 0.03) return null;
  const radius = outerRadius + 24;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#475569" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11} fontWeight={600}>
      {name} {(percent * 100).toFixed(0)}%
    </text>
  );
}

function CustomBarTick({ x, y, payload }: any) {
  useLanguage();
  const label = payload.value || '';
  const parts = label.split('|||');
  const email = parts[0] || '';
  const name = parts[1] || '';
  const truncatedEmail = email.length > 22 ? email.substring(0, 22) + '...' : email;
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fill="#64748b" fontSize={10}>
      <tspan x={x} dy={-4} fontWeight={600}>{truncatedEmail}</tspan>
      <tspan x={x} dy={13} fontSize={9} fill="#94a3b8">{name}</tspan>
    </text>
  );
}

interface UserCostChartsProps {
  report: UserCostReport | null;
  loading: boolean;
  onUserClick: (userId: string) => void;
}

export function UserCostCharts({ report, loading, onUserClick }: UserCostChartsProps) {
  useLanguage();
  const topUsers = useMemo(() => {
    if (!report) return [];
    return [...report.users]
      .sort((a, b) => b.totalCostBRL - a.totalCostBRL)
      .slice(0, 10);
  }, [report]);

  const barData = useMemo(() => {
    return topUsers.map((u) => {
      const fullName = [u.nome, u.sobrenome].filter(Boolean).join(' ') || '';
      const label = u.email ? `${u.email}|||${fullName}` : u.id_user;
      return {
        name: label,
        fullName: u.id_user,
        BRL: u.totalCostBRL,
      };
    });
  }, [topUsers]);

  const pieData = useMemo(() => {
    if (!report) return [];
    return report.chartData.categoryBreakdown.map((c) => ({
      name: c.name,
      value: c.value,
    }));
  }, [report]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[1, 2].map((i) => (
          <div key={i} className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm animate-pulse">
            <div className="h-4 bg-slate-100 rounded w-32 mb-3" />
            <div className="h-64 bg-slate-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* BAR - Top 10 Users */}
      <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col">
        <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider block mb-3">{translateInterfaceText("Top 10 Usuários por Custo")}</span>
        {barData.length > 0 ? (
          <div className="flex-1">
            <ResponsiveContainer width="100%" height={Math.max(380, barData.length * 48)}>
              <BarChart data={barData} layout="vertical" margin={{ left: 0, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={<CustomBarTick />}
                  width={180}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(v: number) => formatBRL(v)}
                  contentStyle={{ fontSize: 11, borderRadius: 8 }}
                />
                <Bar
                  dataKey="BRL"
                  fill="#3b82f6"
                  radius={[0, 4, 4, 0]}
                  barSize={18}
                  onClick={(data) => onUserClick(data.fullName)}
                  className="cursor-pointer"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-xs text-slate-400 text-center py-8">{translateInterfaceText("Sem dados")}</p>
        )}
      </div>

      {/* PIE - Category Breakdown */}
      <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
        <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider block mb-3">{translateInterfaceText("Distribuição por Categoria")}</span>
        {pieData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={85}
                innerRadius={35}
                dataKey="value"
                label={renderCustomLabel}
                labelLine={false}
              >
                {pieData.map((entry, i) => (
                  <Cell key={i} fill={CATEGORY_COLORS[entry.name.toLowerCase()] || PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v: number) => formatBRL(v)} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-xs text-slate-400 text-center py-8">{translateInterfaceText("Sem dados")}</p>
        )}
      </div>
    </div>
  );
}

export default UserCostCharts;
